import { css } from "@emotion/css";
import { Button, Drawer } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ProjectResource } from "@octopusdeploy/octopus-server-client";
import { getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled } from "@octopusdeploy/octopus-server-client";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { GenerateArgoCDAnnotationsDrawer } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/GenerateArgoCDAnnotationsDrawer";
import { GenerateArgoCDHelmAnnotationsDrawer } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDHelmAnnotationsDrawer/GenerateArgoCDHelmAnnotationsDrawer";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { ArgoCDDeploymentPreview } from "~/components/Actions/argocd/ArgoCDDeploymentPreview";
type ArgoCDDeploymentPreviewDrawerInnerProps = {
    analyticsCorrelationId?: string;
    project: ProjectResource;
    onShowGenerateAnnotationsDrawer: () => void;
};
type InnerActions = {
    refresh: () => void;
};
const ArgoCDDeploymentPreviewDrawerInner = forwardRef<InnerActions, ArgoCDDeploymentPreviewDrawerInnerProps>((props: ArgoCDDeploymentPreviewDrawerInnerProps, ref) => {
    const { project, onShowGenerateAnnotationsDrawer } = props;
    const [initiallySelectedHelmApplicationId, setInitiallySelectedHelmApplicationId] = useState<string | undefined>();
    const ephemeralEnvironmentsEnabled = useEphemeralEnvironmentsEnabled();
    const query = useInlineStatusQuery(async (repository) => {
        const environmentsPromise = getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsEnabled, repository);
        const tenantsPromise = repository.Tenants.all();
        const instances = await repository.ArgoCDInstances.list();
        const applicationsFetchPromises = instances.Resources.map((instance) => repository.ArgoCDGateways.listApps(instance.GatewayId));
        const environments = await environmentsPromise;
        const tenants = await tenantsPromise;
        const applications = await Promise.all(applicationsFetchPromises);
        const filteredApps = applications.reduce((accumulator, value) => accumulator.concat(value), []).filter((app) => app.Sources.some((src) => src.ProjectId === project.Id));
        return {
            argoCDInstances: instances.Resources,
            applications: filteredApps,
            context: {
                allEnvironments: environments,
                allTenants: tenants,
            },
        };
    }, [ephemeralEnvironmentsEnabled, project.Id], "Fetch ArgoCD Instances and Applications");
    useImperativeHandle(ref, () => ({
        refresh: () => query.refetch(),
    }));
    return (<>
            <div className={styles.header}>
                The <b>Update Argo CD App Images</b> step in the <b>{project.Name}</b> project will update these applications:
            </div>
            <ArgoCDDeploymentPreview spaceId={project.SpaceId} query={query} onShowHelmAnnotationsDrawer={setInitiallySelectedHelmApplicationId} onShowGenerateAnnotationsDrawer={onShowGenerateAnnotationsDrawer}/>
            <GenerateArgoCDHelmAnnotationsDrawer applications={query.result?.applications || []} initiallySelectedApplicationId={initiallySelectedHelmApplicationId} open={initiallySelectedHelmApplicationId !== undefined} onClose={() => setInitiallySelectedHelmApplicationId(undefined)}/>
        </>);
});
type ArgoCDDeploymentPreviewDrawerProps = Omit<ArgoCDDeploymentPreviewDrawerInnerProps, "onShowGenerateAnnotationsDrawer"> & {
    isOpen: boolean;
    onClose: () => void;
};
export const ArgoCDDeploymentPreviewDrawer = ({ isOpen, onClose, ...rest }: ArgoCDDeploymentPreviewDrawerProps) => {
    const [generateDrawerOpen, setGenerateDrawerOpen] = useState(false);
    const ref = useRef<InnerActions>(null);
    return (<Drawer isOpen={isOpen} onClose={onClose} title={"Deployment Preview"} variant="customActions" customActions={[
            <Button importance={"quiet"} label={"Close"} onClick={() => onClose()}/>,
            <Button importance={"tertiary"} label={"Refresh"} onClick={() => ref.current?.refresh()}/>,
            <Button importance={"secondary"} label={"Generate Scoping Annotations"} onClick={() => setGenerateDrawerOpen(true)}/>,
        ]} width={"wide"} layout={"none"}>
            <>
                <ArgoCDDeploymentPreviewDrawerInner {...rest} ref={ref} onShowGenerateAnnotationsDrawer={() => setGenerateDrawerOpen(true)}/>
                <GenerateArgoCDAnnotationsDrawer open={generateDrawerOpen} onClose={() => setGenerateDrawerOpen(false)}/>
            </>
        </Drawer>);
};
const styles = {
    header: css({
        paddingLeft: space[16],
        paddingBottom: space[16],
        paddingTop: space[16],
    }),
};
